import { ArrowLeft, GlobeEurope } from "@medusajs/icons"
import { Button, Input, Prompt, RadioGroup, Switch, Text, toast } from "@medusajs/ui"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { CountryList, CountryPicker } from "@/components/country-picker"
import { Field } from "@/components/form"
import { EmptyState, ErrorState, SaveBar, Section } from "@/components/ui"
import { countryName } from "@/lib/geo"
import { summarizeCountries, useDeleteMarket, useMarkets, useSaveMarket, type Market, type MarketInput } from "@/lib/markets"
import { useCan } from "@/lib/permissions"
import { errorMessage, sdk } from "@/lib/sdk"
import { Badge, Banner } from "@/terabizz"
import { SettingsGroup, SettingsTitle } from "./settings-general"

type Zone = { id: string; name: string; countries: string[] }
function useZones() {
  return useQuery({ queryKey: ["settings", "shipping"], queryFn: () => sdk.client.fetch<{ zones: Zone[] }>("/admin/terify/shipping") })
}
/** Market countries that no shipping zone covers (customers there can't check out). */
function unshippable(countries: string[], zones: Zone[] | undefined) {
  if (!zones) return []
  const covered = new Set(zones.flatMap((z) => z.countries.map((c) => c.toLowerCase())))
  return countries.filter((c) => !covered.has(c))
}

/** Settings → Markets, like Shopify: where you sell, and how each market is set up. */
export function MarketsSettingsPage() {
  const { data, error } = useMarkets()
  const zones = useZones().data?.zones
  const canWrite = useCan()("settings:write")
  if (error) return <ErrorState error={error} />
  if (!data) return null
  const primary = data.find((m) => m.primary)
  const others = data.filter((m) => !m.primary)
  const missing = unshippable(data.filter((m) => m.status === "active").flatMap((m) => m.countries), zones)
  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <SettingsTitle title="Markets" subtitle="Sell to customers in different countries/regions. Each market sets its countries, how tax is shown and what customers see at checkout." />
        {canWrite && <Button size="small" className="shrink-0 whitespace-nowrap" asChild><Link to="/settings/markets/new">Add market</Link></Button>}
      </div>
      {missing.length > 0 && (
        <div className="mb-4">
          <Banner tone="warning" title={`Customers in ${missing.length} countr${missing.length === 1 ? "y/region" : "ies/regions"} can't check out yet`} action={<Link to="/settings/shipping" className="underline">Set up shipping</Link>}>
            {summarizeCountries(missing.map(countryName))} {missing.length === 1 ? "isn't" : "aren't"} in any shipping zone.
          </Banner>
        </div>
      )}
      {primary && (
        <SettingsGroup title="Primary market" description="Your home market. It can't be turned off or deleted.">
          <Section><MarketRow m={primary} /></Section>
        </SettingsGroup>
      )}
      <SettingsGroup title="Other markets">
        <Section>
          {others.length ? others.map((m) => <MarketRow key={m.id} m={m} />) : (
            <EmptyState title="Sell internationally" body="Add a market to sell to customers in more countries/regions." action={canWrite ? <Button size="small" variant="secondary" asChild><Link to="/settings/markets/new">Add market</Link></Button> : undefined} />
          )}
        </Section>
      </SettingsGroup>
      <SettingsGroup title="Currency">
        <Section>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div>
              <Text size="small" weight="plus">Every market charges US Dollar (USD $)</Text>
              <Text size="xsmall" className="text-ui-fg-subtle">Local currencies can be turned on when the payment provider supports them.</Text>
            </div>
            <span className="shrink-0 whitespace-nowrap rounded-lg bg-ui-bg-component px-2 py-1 text-[12px] font-medium">USD</span>
          </div>
        </Section>
      </SettingsGroup>
    </>
  )
}

function MarketRow({ m }: { m: Market }) {
  return (
    <Link to={`/settings/markets/${m.id}`} className="flex items-center gap-x-3 px-4 py-3 hover:bg-ui-bg-base-hover">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-ui-bg-component"><GlobeEurope /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Text size="small" weight="plus">{m.name}</Text>
          {m.primary ? <Badge tone="info">Primary</Badge> : <Badge tone={m.status === "active" ? "success" : "neutral"}>{m.status === "active" ? "Active" : "Draft"}</Badge>}
        </div>
        <Text size="xsmall" className="truncate text-ui-fg-subtle">
          {[summarizeCountries(m.countries.map(countryName)), m.tax_inclusive ? "Prices include tax" : null].filter(Boolean).join(" · ")}
        </Text>
      </div>
    </Link>
  )
}

const EMPTY: MarketInput = { name: "", status: "active", countries: [], tax_inclusive: false, duties_notice: false }

/** Market detail / Add market. */
export function MarketDetailPage() {
  const { id } = useParams()
  const isNew = !id || id === "new"
  const { data, error } = useMarkets()
  if (error) return <ErrorState error={error} />
  if (!data) return null
  const market = isNew ? null : data.find((m) => m.id === id)
  if (!isNew && !market) return <EmptyState title="Market not found" body="It may have been deleted." action={<Button size="small" variant="secondary" asChild><Link to="/settings/markets">Back to markets</Link></Button>} />
  return <MarketForm key={market?.id ?? "new"} market={market ?? null} all={data} />
}

function MarketForm({ market, all }: { market: Market | null; all: Market[] }) {
  const navigate = useNavigate()
  const canWrite = useCan()("settings:write")
  const base: MarketInput = useMemo(
    () => (market ? { name: market.name, status: market.status, countries: market.countries, tax_inclusive: market.tax_inclusive, duties_notice: market.duties_notice } : EMPTY),
    [market]
  )
  const [v, setV] = useState<MarketInput>(base)
  useEffect(() => setV(base), [base])
  const [picking, setPicking] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const save = useSaveMarket(market?.id)
  const del = useDeleteMarket(market?.id ?? "")
  const zones = useZones().data?.zones
  const taken = useMemo(() => Object.fromEntries(all.filter((m) => m.id !== market?.id).flatMap((m) => m.countries.map((c) => [c, m.name]))), [all, market])
  const dirty = JSON.stringify(v) !== JSON.stringify(base)
  const missing = unshippable(v.countries, zones)
  const set = <K extends keyof MarketInput>(k: K, val: MarketInput[K]) => setV((x) => ({ ...x, [k]: val }))

  const submit = () => {
    if (!v.name.trim()) return toast.error("Give the market a name.")
    if (!v.countries.length) return toast.error("Add at least one country/region.")
    save.mutate(
      { ...v, name: v.name.trim() },
      {
        onSuccess: (m) => {
          toast.success(market ? "Market saved" : "Market created")
          if (!market) navigate(`/settings/markets/${m.id}`, { replace: true })
        },
        onError: (e) => toast.error(errorMessage(e)),
      }
    )
  }

  return (
    <div className="pb-24">
      <div className="mb-6 flex items-center gap-x-2">
        <Link to="/settings/markets" aria-label="Back to markets" className="flex size-7 items-center justify-center rounded-md hover:bg-ui-bg-base-hover"><ArrowLeft /></Link>
        <h1 className="k-h1">{market ? market.name : "Add market"}</h1>
        {market?.primary && <Badge tone="info">Primary</Badge>}
        {market && !market.primary && <Badge tone={market.status === "active" ? "success" : "neutral"}>{market.status === "active" ? "Active" : "Draft"}</Badge>}
      </div>
      <fieldset disabled={!canWrite} className="flex flex-col gap-y-4">
        <Section>
          <div className="px-4 py-3">
            <Field label="Market name" hint="Only you see this, e.g. Europe, United Kingdom, Rest of world.">
              <Input value={v.name} maxLength={60} onChange={(e) => set("name", e.target.value)} />
            </Field>
          </div>
        </Section>

        <Section title="Countries/regions" actions={canWrite ? <Button type="button" size="small" variant="secondary" onClick={() => setPicking(true)}>{v.countries.length ? "Edit" : "Add countries/regions"}</Button> : undefined}>
          <div className="flex flex-col gap-y-3 px-4 py-3">
            <CountryList codes={v.countries} />
            {missing.length > 0 && (
              <Banner tone="warning" title="Shipping isn't set up for every country/region" action={<Link to="/settings/shipping" className="underline">Go to Shipping and delivery</Link>}>
                Customers in {summarizeCountries(missing.map(countryName))} can't check out until a shipping zone includes {missing.length === 1 ? "it" : "them"}.
              </Banner>
            )}
          </div>
        </Section>

        <Section title="Currency">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <Text size="small" className="text-ui-fg-subtle">Customers in this market see prices and pay in US Dollar.</Text>
            <span className="shrink-0 whitespace-nowrap rounded-lg bg-ui-bg-component px-2 py-1 text-[12px] font-medium">USD $</span>
          </div>
        </Section>

        <Section title="Taxes and duties">
          <label className="flex items-start justify-between gap-x-4 px-4 py-3">
            <div>
              <Text size="small" weight="plus">Prices include tax</Text>
              <Text size="xsmall" className="text-ui-fg-subtle">
                Customers pay the price shown and the tax is part of it, as is usual for VAT in Europe and the UK. When off, tax is added at checkout.
              </Text>
            </div>
            <Switch checked={v.tax_inclusive} onCheckedChange={(c) => set("tax_inclusive", c)} />
          </label>
          <label className="flex items-start justify-between gap-x-4 px-4 py-3">
            <div>
              <Text size="small" weight="plus">Show a duties and import taxes notice at checkout</Text>
              <Text size="xsmall" className="text-ui-fg-subtle">Tells customers the carrier may charge duties and import taxes on delivery. They aren't collected at checkout.</Text>
            </div>
            <Switch checked={v.duties_notice} onCheckedChange={(c) => set("duties_notice", c)} />
          </label>
          <div className="px-4 py-3">
            <Link to="/settings/taxes" className="text-[13px] font-medium text-ui-fg-interactive">Set tax rates for these countries/regions</Link>
          </div>
        </Section>

        <Section title="Status">
          <div className="px-4 py-3">
            {market?.primary ? (
              <Text size="small" className="text-ui-fg-subtle">The primary market is always active.</Text>
            ) : (
              <RadioGroup value={v.status} onValueChange={(s) => set("status", s as MarketInput["status"])} className="flex flex-col gap-y-2">
                {[
                  ["active", "Active", "Customers in these countries/regions can buy."],
                  ["draft", "Draft", "Set it up first. Customers there see your primary market and can't check out."],
                ].map(([val, label, hint]) => (
                  <label key={val} className="flex items-start gap-x-2">
                    <RadioGroup.Item value={val} className="mt-0.5" />
                    <div>
                      <Text size="small">{label}</Text>
                      <Text size="xsmall" className="text-ui-fg-subtle">{hint}</Text>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            )}
          </div>
        </Section>

        {market && !market.primary && canWrite && (
          <div>
            <Button type="button" variant="secondary" size="small" className="text-ui-fg-error" onClick={() => setConfirm(true)}>Delete market</Button>
          </div>
        )}
      </fieldset>

      {picking && <CountryPicker selected={v.countries} taken={taken} onClose={() => setPicking(false)} onDone={(codes) => set("countries", codes)} />}
      {canWrite && <SaveBar visible={dirty || !market} message={market ? "Unsaved changes" : "Unsaved market"} saving={save.isPending} onDiscard={() => (market ? setV(base) : navigate("/settings/markets"))} onSave={submit} saveLabel={market ? "Save" : "Create market"} />}
      <Prompt open={confirm} onOpenChange={setConfirm}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {market?.name}?</Prompt.Title>
            <Prompt.Description>Customers in these countries/regions will see your primary market and won't be able to check out. Past orders aren't affected.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action onClick={() => del.mutate(undefined, { onSuccess: () => (toast.success("Market deleted"), navigate("/settings/markets")), onError: (e) => toast.error(errorMessage(e)) })}>Delete market</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </div>
  )
}
